<template>
  <div class="view view-home">
    <div class="container">
      <header class="home-header">
        <h1>Leaderboard</h1>
        <inline-svg name="rackets"></inline-svg>
        <ul class="filter-list">
          <li v-for="item in filterList">
            <button class="filter-button" @click="filter = item.id" :class="{'is-active': filter === item.id}">
              {{item.title}}
            </button>
          </li>
        </ul>
        <p class="games-played" v-if="gamesPlayed">
          Games played since ({{filterHumanDate}}): <b>{{this.gamesPlayed}}</b>
        </p>
      </header>
      <div class="leaderboard" v-if="updatedUsers && usersLoaded">
        <ul class="leaderboard-header">
          <li>
            <span class="first-name"></span>
            <span v-for="title in activeLeaderboardHeader">
              {{title}}
            </span>
          </li>
        </ul>
        <transition-group name="flip-list" tag="ul" class="leaderboard-list">
          <li v-for="(user, index) in sortedUsers" :key="user['.key']">
            <span class="first-name">
              <span>{{index + 1}}.</span>
              <user-avatar :data="user.profile_picture.data.url"></user-avatar>
              <span>{{user.first_name}}</span>
            </span>
            <span>{{user.winPercentage === 0 ? '-': user.winPercentage}}</span>
            <span>{{user.userWins}}/{{user.totalGames - user.userWins}}</span>
            <span class="total-games-cell">{{user.totalGames}}</span>
            <span class="streak-cell">{{user.streak}}</span>
          </li>
        </transition-group>
      </div>
      <div class="buttons-container" v-if="usersLoaded">
        <router-link class="button" to="/new-game">New Game</router-link>
      </div>
    </div>
  </div>
</template>

<script>

  import {db} from '@/utils/firebase'
  import moment from 'moment'
  import {TimelineLite, Sine} from 'gsap'
  import UserAvatar from '@/components/UserAvatar'

  export default {
    components: {
      UserAvatar
    },
    data () {
      return {
        usersLoaded: false,
        isMobile: false,
        leaderboardHeader: ["Win %", "W/L", "Played", "Streak"],
        leaderboardHeaderMobile: ["%", "W/L"],
        filterList: [
          {
            id: 'isoWeek',
            title: 'Week'
          },
          {
            id: 'month',
            title: 'Month'
          },
          {
            id: 'year',
            title: 'Year'
          }
        ],
        filter: 'isoWeek',
        gamesPlayed: false
      }
    },
    firebase: {
      users: {
        source: db.ref('users'),
        readyCallback: function () {
          window.setTimeout(() => {
            this.usersLoaded = true
          }, 100)
        }
      },
      games: db.ref('games'),
      totalGames: db.ref('total_games')
    },
    computed: {
      filterHumanDate () {
        return moment().startOf(this.filter).format('DD/MM/YYYY')
      },
      activeLeaderboardHeader () {
        return this.isMobile ? this.leaderboardHeaderMobile : this.leaderboardHeader
      },
      updatedUsers () {
        return this.users.map(user => {
          user.periodGames = this.filteredUserGames(user)
          user.userWins = this.filteredUserGames(user) ? this.filteredUserGames(user).filter(game => game.winner === user['.key']).length : 0
          user.totalGames = this.filteredUserGames(user).length || 0
          user.streak = this.userStreak(user)
          user.winPercentage = this.winPercentage(user)
          return user
        })
      },
      sortedUsers () {
        return this.updatedUsers.sort((a,b) => {
          if (parseInt(b.winPercentage) === parseInt(a.winPercentage)) {
            return b.totalGames - a.totalGames
          }
          return b.winPercentage - a.winPercentage
        })
      }
    },
    methods: {
      winPercentage (user) {
        if (user.totalGames > 0) {
          return parseFloat(user.userWins / user.totalGames * 100).toFixed(1)
        }
        return 0
      },
      filteredUserGames (user) {
        if (!user.games) return false
        const userGames = Object.keys(user.games).map(game => user.games[game])
        const filteredGames = this.games.filter(game => {
          return moment(game.timestamp).isAfter(moment().startOf(this.filter))
        })
        this.gamesPlayed = filteredGames.length
        let games = []
        userGames.forEach(userGame => {
          let gamePlayed = filteredGames.filter(game => {
            return game['.key'] === userGame
          })
          if (gamePlayed.length > 0) {
            games.push(gamePlayed[0])
          }
        })
        return games
      },
      userWins (user) {
        return this.filteredUserGames(user).filter(game => {
          return game.winner === user['.key']
        }).length
      },
      userStreak (user) {
        const statsArr = Object.keys(user.results).map(item => user.results[item]).reverse()
        let i,
          type,
          streak = 1,
          prevNumber,
          length = statsArr.length;

        for(i = 1; i <= length; i++) {
          prevNumber = statsArr[i-1];
          type = statsArr[i]
          if (statsArr[i] === prevNumber) {
            streak += 1;
            type = statsArr[i]
          } else {
            type = statsArr[i - 1]
            break
          }
        }
        return statsArr.length > 0 ? (type === 1 ? 'W' : 'L') + streak : 0
      },
    },
    mounted () {
      this.isMobile = window.innerWidth < 768
    }
  }

</script>

<style lang="scss" scoped>

  .view-home {
    color: $color-white;
    padding: 5rem 0 10rem 0;
    height: auto;

    @include respond-to(medium) {
      height: 100%;
      padding: 5rem 0 0 0;
    }

  }

  .home-header {
    text-align: center;

    .rackets {
      width: 3.6rem;

      @include respond-to(medium) {
        width: 5rem;
      }

    }

    h1 {
      display: inline-block;
      font-size: 2.4rem;
      font-weight: 900;
      text-transform: uppercase;
      letter-spacing: 5px;
      padding-bottom: 2rem;


      @include respond-to(medium) {
        font-size: 3.6rem;
      }

    }

  }

  .filter-list {
    @include reset-list;
    @include list-inline;
    padding: 2rem 0;

    li:not(:last-child) {
      padding-right: 2rem;
    }

    .filter-button {
      color: $color-primary;
      background-color: transparent;
      padding: 1rem 2rem;

      &.is-active {
        background-color: $color-primary;
        color: $color-white;
      }


    }

  }

  .leaderboard {
    position: relative;
    background-color: $color-primary;
    padding: 4rem 2rem;
    margin: 0 -2rem;

    @include respond-to(medium) {
      padding: 2rem 4rem 0 4rem;
      height: 53vh;
      overflow-y: auto;
      border: 20px solid;
      margin: 0;
    }

    @include respond-to(medium) {
      &:after {
        content: '';
        position: absolute;
        top: 0;
        left: 50%;
        width: 10px;
        margin-left: -5px;
        background-color: $color-white;
        height: 100%;
      }
    }

    .leaderboard-header,
    .leaderboard-list
    {
      @include reset-list;

      li {
        display: flex;
        margin-bottom: 1rem;
        /*justify-content: space-between;*/
      }

      .first-name {
        width: 50%;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;

        span {
          width: auto;
          min-width: 2rem;
          display: inline-block;
          vertical-align: middle;

          @include respond-to(medium) {
            min-width: 4rem;
          }

        }

      }

      span {
        width: (50% / 2);
      }

      .streak-cell,
      .total-games-cell
      {
        display: none;
      }

      @include respond-to(medium) {
        .first-name {
          width: 60%;
        }

        span {
          width: 11%;
        }

        .streak-cell,
        .total-games-cell
        {
          display: inline-block;
        }

      }

    }

    .leaderboard-header {
      font-size: 1.2rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 1px;
      padding-bottom: 3rem;

      @include respond-to(medium) {
        font-size: 1.8rem;
      }

    }

    .leaderboard-list {
      position: relative;
      font-size: 1.8rem;
      padding-bottom: 4rem;

      @include respond-to(medium) {
        font-size: 2.8rem;
        &:after {
          content: '';
          position: absolute;
          top: 0;
          left: 50%;
          width: 10px;
          margin-left: -5px;
          background-color: $color-white;
          height: 100%;
        }
      }

    }

  }

  .buttons-container {
    text-align: center;
    padding: 3rem 0;
  }

  .games-played {
    font-size: 1.2rem;
    font-style: italic;
    padding-bottom: 2rem;

    @include respond-to(medium) {
      font-size: 1.6rem;
    }

  }

  .flip-list-move {
    /*transition: transform 1s ease(easeInOutQuart);*/
    opacity: 0.6;
    transition: transform 1.3s ease(easeInOutBack);
  }


</style>



// WEBPACK FOOTER //
// src/components/Home.vue